import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Eye, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
});

const AdminReservations = () => {
  const [reservations, setReservations] = useState([]);
  const [circuits, setCircuits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const [resRes, circuitsRes] = await Promise.all([
        axios.get(`${API}/admin/reservations`, { ...getAuthHeaders(), params }),
        axios.get(`${API}/admin/circuits`, getAuthHeaders()),
      ]);
      setReservations(resRes.data);
      setCircuits(circuitsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const getCircuitName = (circuitId) => {
    const circuit = circuits.find(c => c.id === circuitId);
    return circuit?.title?.fr || "Circuit inconnu";
  };

  const handleUpdateStatus = async (reservationId, status) => {
    try {
      await axios.put(`${API}/admin/reservations/${reservationId}`, { status }, getAuthHeaders());
      toast.success("Statut mis à jour - Email envoyé au client");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleCancel = async (reservationId) => {
    if (!window.confirm("Annuler cette réservation ? Le client sera notifié par email.")) return;
    try {
      await axios.put(`${API}/admin/reservations/${reservationId}`, { status: "cancelled" }, getAuthHeaders());
      toast.success("Réservation annulée - Email envoyé au client");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (reservationId) => {
    if (!window.confirm("Supprimer définitivement cette réservation ? Cette action est irréversible et le client sera notifié par email.")) return;
    try {
      await axios.delete(`${API}/admin/reservations/${reservationId}`, getAuthHeaders());
      toast.success("Réservation supprimée - Email envoyé au client");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleUpdateNotes = async (notes) => {
    if (!selectedReservation) return;
    try {
      await axios.put(`${API}/admin/reservations/${selectedReservation.id}`, { admin_notes: notes }, getAuthHeaders());
      toast.success("Notes mises à jour");
      setDetailOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      confirmed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
      completed: "bg-blue-100 text-blue-800",
    };
    const labels = {
      pending: "En attente",
      confirmed: "Confirmé",
      cancelled: "Annulé",
      completed: "Terminé",
    };
    return <Badge className={colors[status] || "bg-gray-100"}>{labels[status] || status}</Badge>;
  };

  const getPaymentBadge = (status) => {
    const colors = {
      unpaid: "bg-red-100 text-red-800",
      paid: "bg-green-100 text-green-800",
      refunded: "bg-gray-100 text-gray-800",
    };
    return <Badge className={colors[status] || "bg-gray-100"}>{status}</Badge>;
  };

  const filteredReservations = reservations.filter(r => {
    const name = r.user_name || r.customer_name || "";
    const email = r.user_email || r.customer_email || "";
    return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           email.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="admin-reservations">
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-bold">Réservations</h1>
        <p className="text-muted-foreground">Gérez les réservations clients</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="confirmed">Confirmé</SelectItem>
            <SelectItem value="cancelled">Annulé</SelectItem>
            <SelectItem value="completed">Terminé</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-card rounded-xl border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Circuit</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Participants</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Paiement</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredReservations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  Aucune réservation trouvée
                </TableCell>
              </TableRow>
            ) : (
              filteredReservations.map((reservation) => (
                <TableRow key={reservation.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{reservation.user_name || reservation.customer_name}</p>
                      <p className="text-sm text-muted-foreground">{reservation.user_email || reservation.customer_email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {reservation.circuit_id ? getCircuitName(reservation.circuit_id) : 
                     reservation.stage_id ? <Badge variant="outline">Stage</Badge> : "-"}
                  </TableCell>
                  <TableCell>{reservation.preferred_date || reservation.created_at?.split("T")[0] || "-"}</TableCell>
                  <TableCell>{reservation.num_travelers || reservation.num_participants || 1}</TableCell>
                  <TableCell className="font-semibold">€{reservation.total_price}</TableCell>
                  <TableCell>{getStatusBadge(reservation.status)}</TableCell>
                  <TableCell>{getPaymentBadge(reservation.payment_status)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setSelectedReservation(reservation); setDetailOpen(true); }} data-testid={`view-reservation-${reservation.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {reservation.status === "pending" && (
                        <>
                          <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleUpdateStatus(reservation.id, "confirmed")} data-testid={`confirm-reservation-${reservation.id}`}>
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleCancel(reservation.id)} data-testid={`cancel-reservation-${reservation.id}`}>
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(reservation.id)} data-testid={`delete-reservation-${reservation.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Détails de la réservation</DialogTitle>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Client</Label>
                  <p className="font-medium">{selectedReservation.user_name || selectedReservation.customer_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p>{selectedReservation.user_email || selectedReservation.customer_email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Téléphone</Label>
                  <p>{selectedReservation.user_phone || selectedReservation.customer_phone || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Date</Label>
                  <p>{selectedReservation.preferred_date || selectedReservation.created_at?.split("T")[0] || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Participants</Label>
                  <p>{selectedReservation.num_travelers || selectedReservation.num_participants || 1}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Total</Label>
                  <p className="font-bold text-lg">€{selectedReservation.total_price}</p>
                </div>
              </div>
              {selectedReservation.special_requests && (
                <div>
                  <Label className="text-muted-foreground">Demandes spéciales</Label>
                  <p className="bg-muted p-2 rounded">{selectedReservation.special_requests}</p>
                </div>
              )}
              <div>
                <Label>Notes admin</Label>
                <Textarea
                  defaultValue={selectedReservation.admin_notes || ""}
                  placeholder="Ajouter des notes..."
                  onBlur={(e) => handleUpdateNotes(e.target.value)}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReservations;
